import {computed, inject, Injectable, OnDestroy, signal, effect} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Subject, Observable, from, EMPTY, of, timer, switchMap, catchError, tap, retry, defer, throwError, take} from 'rxjs';
// @ts-ignore
import {HubConnection, HubConnectionBuilder, LogLevel} from '@microsoft/signalr';
import {OAuthService} from 'angular-oauth2-oidc';
import {QueueApiService} from '../../api/queue/queue-api.service';
import {UserDataService} from '../../ui-state/user-data/user-data.service';
import {environment} from '../../../../environments/environment';

export type ConnectionState = 'Disconnected' | 'Connecting' | 'Connected' | 'Reconnecting' | 'Failed';

export interface QueueChangeData {
  userId?: string;
  queueId?: string;
  changeType?: 'assignment' | 'permission' | 'availability';
  timestamp?: string;
}

@Injectable({
  providedIn: 'root'
})
export class QueueSignalrService implements OnDestroy {
  // Dependency Injection
  private readonly oAuthService = inject(OAuthService);
  private readonly queueApiService = inject(QueueApiService);
  private readonly userDataService = inject(UserDataService);

  // Declare Sibjects
  private hubConnection?: HubConnection;
  private queueChangeSubject$ = new Subject<QueueChangeData>();
  private connectionSubject$ = new Subject<ConnectionState>();

  // Signals for state management
  private readonly _connectionState = signal<ConnectionState>('Disconnected');
  private readonly _isInitialized = signal<boolean>(false);
  private readonly _lastQueueChange = signal<QueueChangeData | null>(null);
  private readonly _connectionError = signal<string | null>(null);

  // Public readonly signals
  public readonly connectionState = this._connectionState.asReadonly();
  public readonly isInitialized = this._isInitialized.asReadonly();
  public readonly lastQueueChange = this._lastQueueChange.asReadonly();
  public readonly connectionError = this._connectionError.asReadonly();

  // Computed signals
  public readonly isConnected = computed(() => this._connectionState() === 'Connected');
  public readonly canReceiveUpdates = computed(() => this.isConnected());
  public readonly connectionStatus = computed(() => {
    const state = this._connectionState();
    const error = this._connectionError();

    return {
      state,
      isConnected: state === 'Connected',
      hasError: !!error,
      errorMessage: error,
      canRetry: state === 'Failed' || state === 'Disconnected'
    };
  });

  // Observable streams
  public readonly connectionState$ = this.connectionSubject$.asObservable();
  public readonly queueChanges$ = this.queueChangeSubject$.asObservable();

  constructor() {
    // Effect to handle queue changes and refresh user assignments
    effect(() => {
      const queueChange = this._lastQueueChange();
      const currentUser = this.userDataService.currentUser();

      if (queueChange && currentUser?.id && this.isConnected()) {
        if (!environment.production) {
          console.log('Queue change detected, refreshing user assignments:', queueChange);
        }
        this.refreshUserQueueAssignments$(currentUser.id).subscribe();
      }
    });

    // Effect to log connection state changes
    effect(() => {
      const state = this._connectionState();
      if (!environment.production) {
        console.log(`SignalR connection state changed: ${state}`);
      }
    });

    // Handle queue changes from SignalR
    this.queueChangeSubject$
      .pipe(takeUntilDestroyed())
      .subscribe(data => {
        this._lastQueueChange.set(data);
      });

    // Keep connection state signal in sync with observable
    this.connectionSubject$
      .pipe(takeUntilDestroyed())
      .subscribe(state => {
        this._connectionState.set(state);
      });
  }

  /**
   * Initializes the signalR connection
   */
  public init$(): Observable<void> {
    if (this._isInitialized() && this.isConnected()) {
      console.log('SignalR already initialized and connected');
      return of(void 0);
    }

    this._connectionError.set(null);

    return this.disconnect$().pipe(
      switchMap(() => this.createConnection$()),
      switchMap(() => this.startConnection$()),
      tap(() => {
        this._isInitialized.set(true);
      }),
      catchError((error: any) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown connection error';
        this._connectionError.set(errorMessage);
        this.connectionSubject$.next('Failed');
        return throwError(() => error);
      })
    );
  }

  /**
   * Reconnects by reinitializing.
   */
  public reconnect$(): Observable<void> {
    if (!environment.production) {
      console.log('Attempting to reconnect SignalR...');
    }
    return this.init$();
  }

  /**
   * Creates an observable to retry the connection.
   * @param maxRetries
   * @param delayMs
   */
  public reconnectWithRetry$(maxRetries: number = 3, delayMs: number = 2000): Observable<void> {
    return this.reconnect$().pipe(
      retry({
        count: maxRetries,
        delay: (error: any, retryCount: number) => {
          if (!environment.production) {
            console.log(`Reconnection attempt ${retryCount} failed, retrying in ${delayMs}ms...`);
          }
          return timer(delayMs * retryCount); // Exponential backoff
        }
      }),
      catchError((error: any) => {
        console.error(`Failed to reconnect after ${maxRetries} attempts:`, error);
        throw error;
      })
    );
  }

  /**
   * Creates the connection. Uses defer to wait until a subscriber subscribes to create the observable.
   * @private
   */
  private createConnection$(): Observable<void> {
    return defer(() => {
      const authToken = this.oAuthService.getAccessToken();
      if (!authToken) {
        throw new Error('No authentication token available');
      }
      if (!environment.production) {
        console.log('Creating SignalR connection with token present:', !!authToken);
      }

      this.hubConnection = new HubConnectionBuilder()
        .withUrl(`${environment.ApiServer}/mainhub`, {
          accessTokenFactory: () => {
            const token = this.oAuthService.getAccessToken();
            if (!environment.production) {
              console.log('Token factory called, returning token:', !!token);
            }
            return token;
          }
        })
        .configureLogging(LogLevel.Information)
        .withAutomaticReconnect([0, 2000, 10000, 30000]) // Retry intervals
        .build();
      this.setupEventHandlers();
      return of(void 0);
    });
  }

  /**
   * Sets up event handlers used by the hub connection.
   * @private
   */
  private setupEventHandlers(): void {
    if (!this.hubConnection) return;

    // Connection lifecycle events
    this.hubConnection.onclose((error: Error | undefined) => {
      if (!environment.production) {
        console.warn('SignalR connection closed', error);
      }
      this.connectionSubject$.next('Disconnected');
      if (error) {
        this._connectionError.set(error.message);
      }
    });
    this.hubConnection.onreconnecting((error: Error | undefined) => {
      if (!environment.production) {
        console.warn('SignalR reconnecting...', error);
      }
      this.connectionSubject$.next('Reconnecting');
    });
    this.hubConnection.onreconnected((connectionId: string | undefined) => {
      if (!environment.production) {
        console.log('SignalR reconnected with ID:', connectionId);
      }
      this.connectionSubject$.next('Connected');
      this._connectionError.set(null);
    });
    // Business logic events
    this.hubConnection.on('QueueChange', (data: QueueChangeData) => {
      if (!environment.production) {
        console.log('Received QueueChange event:', data);
      }
      this.queueChangeSubject$.next({
        ...data,
        timestamp: new Date().toISOString()
      });
    });
    // Additional events you might want to handle
    this.hubConnection.on('PermissionChanged', (data: any) => {
      if (!environment.production) {
        console.log('Received PermissionChanged event:', data);
      }
      // Handle permission changes specifically
      this.queueChangeSubject$.next({
        changeType: 'permission',
        ...data,
        timestamp: new Date().toISOString()
      });
    });
  }

  /**
   * Starts the connection.
   * @private
   */
  private startConnection$(): Observable<void> {
    if (!this.hubConnection) {
      return throwError(() => new Error('Hub connection not initialized'));
    }
    this.connectionSubject$.next('Connecting');
    return from(this.hubConnection.start() as Promise<void>).pipe(
      tap(() => {
        this.connectionSubject$.next('Connected');
        this._connectionError.set(null);
        if (!environment.production) {
          console.log('SignalR connection established successfully');
          console.log('Connected to SignalR hub with authenticated user');
        }
      }),
      catchError((error: any) => {
        const errorMessage = error instanceof Error ? error.message : 'Connection failed';
        console.error('SignalR connection failed:', error);
        this.connectionSubject$.next('Failed');
        this._connectionError.set(errorMessage);
        return throwError(() => error);
      })
    );
  }

  /**
   * Refresh user's queue assignments by reaching out to server and setting it's updated assignments.
   * @param userId
   * @private
   */
  private refreshUserQueueAssignments$(userId: string): Observable<void> {
    return this.queueApiService.getUserAssignedQueues(userId).pipe(
      tap(queueAssignments => {
        this.userDataService.setUserQueueAssignments(queueAssignments);
        if (!environment.production) {
          console.log('User queue assignments refreshed successfully');
        }
      }),
      catchError((error: any) => {
        console.error('Failed to refresh user queue assignments:', error);
        return EMPTY; // Continue the stream even if this fails
      }),
      switchMap(() => of(void 0)) // Convert to void return type
    );
  }

  /**
   * Disconnects signalR and checks for any errors.
   */
  public disconnect$(): Observable<void> {
    if (!this.hubConnection) {
      return of(void 0);
    }

    return from(this.hubConnection.stop() as Promise<void>).pipe(
      tap(() => {
        if (!environment.production) {
          console.log('SignalR connection stopped');
        }
      }),
      catchError((error: any) => {
        console.error('Error stopping SignalR connection:', error);
        return of(void 0); // Continue cleanup even if stop fails
      }),
      tap(() => {
        this.hubConnection = undefined;
        this.connectionSubject$.next('Disconnected');
      })
    );
  }

  /**
   *
   * @param methodName
   * @param args
   */
  public sendMessage$(methodName: string, ...args: any[]): Observable<void> {
    if (!this.hubConnection || !this.isConnected()) {
      return throwError(() => new Error('SignalR connection not available'));
    }
    return from(this.hubConnection.invoke(methodName, ...args) as Promise<void>).pipe(
      catchError((error: any) => {
        console.error(`Failed to send message '${methodName}':`, error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Observable-based connection monitoring
   */
  public waitForConnection$(): Observable<void> {
    if (this.isConnected()) {
      return of(void 0);
    }
    return this.connectionState$.pipe(
      switchMap(state => {
        if (state === 'Connected') {
          return of(void 0);
        } else if (state === 'Failed') {
          return throwError(() => new Error('Connection failed'));
        }
        return EMPTY;
      }),
      take(1)
    );
  }

  /**
   * Convenience method for components that need to ensure connection before operations
   */
  public ensureConnected$(): Observable<void> {
    return this.isConnected() ? of(void 0) : this.init$();
  }

  /**
   * Utility method to get connection info
   */
  public getConnectionInfo() {
    return computed(() => ({
      connectionId: this.hubConnection?.connectionId ?? null,
      state: this._connectionState(),
      isInitialized: this._isInitialized(),
      lastChange: this._lastQueueChange(),
      error: this._connectionError()
    }));
  }

  /**
   * Life cycle hook that completes the subjects and disconnects signalR on destroy.
   */
  ngOnDestroy(): void {
    if (!environment.production) {
      console.log('QueueSignalrService destroying...');
    }
    this.queueChangeSubject$.complete();
    this.connectionSubject$.complete();
    this.disconnect$().subscribe();
  }
}
